import React, { useState } from 'react';
import { categories, companyInfo } from '../../data/mock';
import CategoryModal from '../CategoryModal';
import * as LucideIcons from 'lucide-react';

const Home = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  return (
    <section id="home" className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center pt-16">
      {/* Container responsive - pleine largeur sur desktop, avec padding sur mobile */}
      <div className="w-full px-2 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-8">
        <div className="text-center mb-6 sm:mb-8 lg:mb-12">
          {/* Logo responsive */}
          <div className="mb-3 sm:mb-4 animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_sisens-cuisine/artifacts/3lootc7r_25.04.10%20SISENS%20LOGO.png" 
              alt="SISENS Logo" 
              className="h-10 sm:h-12 lg:h-16 xl:h-20 w-auto mx-auto transition-transform duration-500 hover:scale-110"
            />
          </div>

          {/* Tagline responsive */}
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-2 sm:mb-3 lg:mb-4 animate-slide-up px-2">
            {companyInfo.tagline}
          </h1>
          
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-xs sm:max-w-2xl lg:max-w-4xl mx-auto mb-4 sm:mb-6 lg:mb-8 animate-slide-up-delay px-4">
            {companyInfo.description}
          </p>
        </div>

        {/* Categories Grid - responsive avec marges sur desktop */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-1 sm:gap-2 lg:gap-3 w-full max-w-sm sm:max-w-full lg:max-w-5xl xl:max-w-6xl mx-auto">
          <style jsx>{`
            .card-grid > * {
              min-height: 140px;
            }
          `}</style>
          {categories.map((category, index) => {
            const IconComponent = LucideIcons[category.icon] || LucideIcons.Circle;
            
            return (
              <div
                key={category.id}
                onClick={() => openModal(category)}
                className="relative overflow-hidden rounded-lg sm:rounded-xl shadow-sm border border-gray-100 cursor-pointer transition-all duration-500 hover:shadow-lg hover:scale-105 hover:border-[#4b9fcc]/30 group animate-fade-in-up min-h-[120px] sm:min-h-[140px] lg:min-h-[180px] xl:min-h-[220px] aspect-[4/3] flex items-end"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Image d'arrière-plan pour Hospitality restauration */}
                {category.id === 1 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/1n77bgdb_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Image d'arrière-plan pour Restauration d'entreprise */}
                {category.id === 2 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/ijpeyt11_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Image d'arrière-plan pour Restauration scolaire */}
                {category.id === 3 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/bhmqwtsi_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Image d'arrière-plan pour Restauration commerciale */}
                {category.id === 4 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/5g48b0kl_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Image d'arrière-plan pour Café-Epicerie */}
                {category.id === 5 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/jslzkqah_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Image d'arrière-plan pour Service Daily Pronto */}
                {category.id === 6 && (
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundImage: `url('https://customer-assets.emergentagent.com/job_15cba88e-9623-4150-b1b8-7d712e58aefe/artifacts/x79xw91m_image.png')`,
                      filter: 'blur(0.5px)',
                    }}
                  ></div>
                )}
                
                {/* Zone de texte en bas avec fond bleu très transparent */}
                <div className="absolute bottom-0 left-0 right-0">
                  <div className="bg-[#4b9fcc]/30 group-hover:bg-[#4b9fcc]/50 backdrop-blur-sm p-2 sm:p-3 lg:p-4 min-h-[2.5rem] sm:min-h-[3rem] lg:min-h-[3.5rem] xl:min-h-[4rem] flex items-center justify-center transition-all duration-300">
                    {/* Titre principal responsive */}
                    <div className="text-center">
                      <h3 className={`font-semibold leading-tight text-white px-1 ${
                        category.id === 1 ? "text-xs sm:text-sm lg:text-base xl:text-lg" :  // Hospitality restauration
                        category.id === 2 ? "text-xs sm:text-xs lg:text-sm xl:text-base" :  // Restauration d'entreprise 
                        category.id === 3 ? "text-xs sm:text-xs lg:text-sm xl:text-base" :  // Restauration scolaire
                        category.id === 4 ? "text-xs sm:text-sm lg:text-base xl:text-lg" :  // Restauration commerciale  
                        category.id === 5 ? "text-xs sm:text-sm lg:text-base xl:text-lg" :  // Café-Epicerie
                        category.id === 6 ? "text-xs sm:text-sm lg:text-base xl:text-lg" : "text-xs sm:text-sm"  // Service Daily Pronto
                      }`}>
                        {category.title}
                      </h3>
                      <p className="text-white/70 text-xs font-light italic -mt-0.5">
                        En savoir plus…
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      <CategoryModal 
        category={selectedCategory}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </section>
  );
};

export default Home;